// Shared API client functions for Building Code Consultant apps

import type {
  ApiResponse,
  Article,
  ChatRequest,
  ChatResponse,
  CodeSearchResult,
  CostEstimate,
  FeasibilityReport,
  GovernmentIncentive,
  PaginatedResponse,
  PermitRequirement,
  QuantitySurveyProject,
  RateLimitError,
  RiskAssessment,
  RiskFactor,
  RiskLevel,
  SDABCase,
} from '@bcc/shared-types';

// Re-export RateLimitError for consumers
export type { RateLimitError } from '@bcc/shared-types';

// ============================================
// Configuration
// ============================================

let baseUrl = import.meta.env?.VITE_API_URL || 'http://localhost:8002';

export function setApiBaseUrl(url: string) {
  baseUrl = url;
}

// Custom error class for API errors with response data
class ApiError extends Error {
  response?: {
    status: number;
    data?: RateLimitError;
  };

  constructor(message: string, status: number, data?: RateLimitError) {
    super(message);
    this.name = 'ApiError';
    this.response = { status, data };
  }
}

async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const response = await fetch(`${baseUrl}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));

    // Handle rate limit errors specially
    if (response.status === 429) {
      const error = new ApiError(
        errorData.detail?.error || errorData.error || 'Rate limit exceeded',
        response.status,
        errorData.detail || errorData
      );
      throw error;
    }

    throw new ApiError(
      errorData.detail?.error || errorData.detail || `API Error: ${response.status}`,
      response.status
    );
  }

  return response.json();
}

// ============================================
// Chat API
// ============================================

interface BackendChatResponse {
  answer: string;
  answer_with_citations?: string;
  sources?: Array<{
    source_type: string;
    source_id: string;
    source_title: string;
    source_reference: string;
    excerpt: string;
    relevance_score: number;
    citation_number: number;
  }>;
  references?: Array<{
    number: number;
    source_type: string;
    reference: string;
    title: string;
    source_id: string;
  }>;
  conversation_id?: string;
}

export async function sendChatMessage(
  request: ChatRequest
): Promise<ChatResponse> {
  const backendResponse = await fetchApi<BackendChatResponse>('/api/v1/chat/ask', {
    method: 'POST',
    body: JSON.stringify(request),
  });

  // Transform backend response to expected ChatResponse format
  const citations: ChatResponse['citations'] = (backendResponse.sources || []).map((source) => ({
    article_number: source.source_reference,
    code_name: source.source_type,
    title: source.source_title,
    relevance_score: source.relevance_score,
  }));

  return {
    response: backendResponse.answer_with_citations || backendResponse.answer,
    conversation_id: backendResponse.conversation_id || request.conversation_id || '',
    citations,
  };
}

export async function getChatHistory(
  conversationId: string
): Promise<ApiResponse<{ messages: ChatResponse[] }>> {
  return fetchApi(`/api/v1/chat/history/${conversationId}`);
}

// ============================================
// Code Exploration API
// ============================================

// Backend response types (different from frontend types)
interface BackendSearchResult {
  query: string;
  total_results: number;
  results: Array<{
    id: string;
    article_number: string;
    title: string;
    full_text: string;
    code_short_name: string;
    code_version?: string;
    relevance_score?: number;
    highlight?: string;
  }>;
  search_type: string;
}

export async function searchCodes(
  query: string,
  options?: {
    code_name?: string;
    limit?: number;
    offset?: number;
  }
): Promise<CodeSearchResult> {
  const params = new URLSearchParams({ query });
  if (options?.code_name) params.append('code_name', options.code_name);
  if (options?.limit) params.append('limit', String(options.limit));
  if (options?.offset) params.append('offset', String(options.offset));

  const response = await fetchApi<BackendSearchResult>(`/api/v1/explore/search?${params}`);

  // Transform backend response to frontend types
  return {
    query: response.query,
    total_count: response.total_results,
    articles: response.results.map(r => ({
      id: r.id,
      article_number: r.article_number,
      title: r.title,
      full_text: r.full_text,
      code_name: r.code_short_name,
    })),
  };
}

export async function getArticle(articleId: string): Promise<Article> {
  return fetchApi(`/api/v1/explore/article/${articleId}`);
}

export async function getCodeStructure(
  codeName: string
): Promise<ApiResponse<{ parts: string[]; divisions: string[] }>> {
  return fetchApi(`/api/v1/explore/structure/${codeName}`);
}

export async function browseCode(
  codeName: string,
  options?: {
    part?: string;
    division?: string;
    section?: string;
  }
): Promise<PaginatedResponse<Article>> {
  const params = new URLSearchParams({ code_name: codeName });
  if (options?.part) params.append('part', options.part);
  if (options?.division) params.append('division', options.division);
  if (options?.section) params.append('section', options.section);

  return fetchApi(`/api/v1/explore/browse?${params}`);
}

// ============================================
// Permit Requirements API
// ============================================

export async function getPermitRequirements(
  category: 'residential' | 'commercial',
  projectType?: string
): Promise<PermitRequirement[]> {
  const params = new URLSearchParams({ category });
  if (projectType) params.append('project_type', projectType);

  return fetchApi(`/api/v1/permits/requirements?${params}`);
}

export async function calculatePermitFees(
  permitType: string,
  projectValue: number
): Promise<ApiResponse<{ estimated_fee: number; breakdown: object }>> {
  return fetchApi('/api/v1/permits/calculate-fee', {
    method: 'POST',
    body: JSON.stringify({ permit_type: permitType, project_value: projectValue }),
  });
}

// ============================================
// SDAB / Development Permit API
// ============================================

export async function searchSDABCases(
  query: string,
  options?: {
    decision?: string;
    year?: number;
    limit?: number;
  }
): Promise<PaginatedResponse<SDABCase>> {
  const params = new URLSearchParams({ query });
  if (options?.decision) params.append('decision', options.decision);
  if (options?.year) params.append('year', String(options.year));
  if (options?.limit) params.append('limit', String(options.limit));

  return fetchApi(`/api/v1/sdab/search?${params}`);
}

export async function assessDevelopmentRisk(
  projectDetails: {
    address: string;
    proposed_use: string;
    lot_area_sqm: number;
    building_area_sqm: number;
    height_m: number;
    setbacks: {
      front: number;
      rear: number;
      side: number;
    };
    parking_spaces: number;
  }
): Promise<RiskAssessment> {
  // Generate mock risk assessment based on project details
  // (API endpoint not implemented yet)
  return generateMockRiskAssessment(projectDetails);
}

// Mock risk assessment generator
function generateMockRiskAssessment(projectDetails: {
  address: string;
  proposed_use: string;
  lot_area_sqm: number;
  building_area_sqm: number;
  height_m: number;
  setbacks: { front: number; rear: number; side: number };
  parking_spaces: number;
}): RiskAssessment {
  const factors: RiskFactor[] = [];
  let riskScore = 0;

  // Analyze lot coverage
  // Impact uses RiskLevel where HIGH = favorable (green), LOW = unfavorable (red)
  const lotCoverage = (projectDetails.building_area_sqm / projectDetails.lot_area_sqm) * 100;
  if (lotCoverage > 45) {
    factors.push({
      category: 'Lot Coverage',
      description: `Building coverage is ${lotCoverage.toFixed(1)}% of lot area`,
      impact: lotCoverage > 60 ? 'LOW' : 'MEDIUM', // HIGH risk = LOW approval likelihood
      details: 'Most residential zones limit lot coverage to 40-45%. Higher coverage may require a relaxation.',
    });
    riskScore += lotCoverage > 60 ? 25 : 15;
  } else {
    factors.push({
      category: 'Lot Coverage',
      description: `Building coverage is ${lotCoverage.toFixed(1)}% of lot area`,
      impact: 'HIGH', // LOW risk = HIGH approval likelihood
      details: 'Lot coverage is within typical limits for most zones.',
    });
  }

  // Analyze height
  if (projectDetails.height_m > 10) {
    factors.push({
      category: 'Building Height',
      description: `Proposed height is ${projectDetails.height_m}m`,
      impact: projectDetails.height_m > 14 ? 'LOW' : 'MEDIUM',
      details: 'Buildings over 10m may face neighbour concerns about shadowing and privacy.',
    });
    riskScore += projectDetails.height_m > 14 ? 20 : 10;
  } else {
    factors.push({
      category: 'Building Height',
      description: `Proposed height is ${projectDetails.height_m}m`,
      impact: 'HIGH',
      details: 'Height is within typical limits for low-density residential zones.',
    });
  }

  // Analyze setbacks
  const minSetback = Math.min(projectDetails.setbacks.front, projectDetails.setbacks.rear, projectDetails.setbacks.side);
  if (projectDetails.setbacks.front < 3) {
    factors.push({
      category: 'Front Setback',
      description: `Front setback is ${projectDetails.setbacks.front}m`,
      impact: projectDetails.setbacks.front < 1.5 ? 'LOW' : 'MEDIUM',
      details: 'Front setbacks below 3m often require relaxation in residential zones.',
    });
    riskScore += projectDetails.setbacks.front < 1.5 ? 20 : 10;
  }

  if (projectDetails.setbacks.rear < 6) {
    factors.push({
      category: 'Rear Setback',
      description: `Rear setback is ${projectDetails.setbacks.rear}m`,
      impact: projectDetails.setbacks.rear < 3 ? 'LOW' : 'MEDIUM',
      details: 'Rear setbacks below 7.5m may affect neighbouring properties.',
    });
    riskScore += projectDetails.setbacks.rear < 3 ? 15 : 8;
  }

  if (projectDetails.setbacks.side < 1.2) {
    factors.push({
      category: 'Side Setback',
      description: `Side setback is ${projectDetails.setbacks.side}m`,
      impact: 'MEDIUM',
      details: 'Side setbacks below 1.2m are common relaxation requests.',
    });
    riskScore += 10;
  }

  // Analyze parking
  const estimatedRequiredParking = Math.ceil(projectDetails.building_area_sqm / 50);
  if (projectDetails.parking_spaces < estimatedRequiredParking) {
    factors.push({
      category: 'Parking',
      description: `${projectDetails.parking_spaces} spaces provided (est. ${estimatedRequiredParking} required)`,
      impact: projectDetails.parking_spaces < estimatedRequiredParking / 2 ? 'LOW' : 'MEDIUM',
      details: 'Parking shortfalls often face community opposition, especially in established neighbourhoods.',
    });
    riskScore += projectDetails.parking_spaces < estimatedRequiredParking / 2 ? 20 : 10;
  } else {
    factors.push({
      category: 'Parking',
      description: `${projectDetails.parking_spaces} spaces provided`,
      impact: 'HIGH',
      details: 'Parking appears adequate for the proposed use.',
    });
  }

  // Determine overall approval likelihood (inverse of risk)
  // RiskLevel here represents APPROVAL likelihood: HIGH = high approval chance, LOW = low approval chance
  let overallRisk: RiskLevel;
  if (riskScore >= 50) {
    overallRisk = 'LOW'; // High risk = low approval likelihood
  } else if (riskScore >= 25) {
    overallRisk = 'MEDIUM';
  } else {
    overallRisk = 'HIGH'; // Low risk = high approval likelihood
  }

  // Calculate approval probability
  const approvalProbability = Math.max(25, Math.min(95, 100 - riskScore));

  // Generate recommendations
  const recommendations: string[] = [];
  if (lotCoverage > 45) {
    recommendations.push('Consider reducing building footprint to meet lot coverage requirements.');
  }
  if (projectDetails.height_m > 10) {
    recommendations.push('Prepare shadow study and visual impact assessment for neighbours.');
  }
  if (minSetback < 3) {
    recommendations.push('Document how setback relaxations are consistent with neighbourhood character.');
  }
  if (projectDetails.parking_spaces < estimatedRequiredParking) {
    recommendations.push('Consider Transportation Demand Management plan to justify reduced parking.');
  }
  recommendations.push('Engage with neighbours early in the process to address concerns.');
  recommendations.push('Review similar approved SDAB cases in your area for precedent.');

  // Generate mock similar cases
  const similarCases: SDABCase[] = generateMockSimilarCases(projectDetails.proposed_use);

  return {
    overall_risk: overallRisk,
    approval_probability: approvalProbability,
    factors,
    similar_cases: similarCases,
    recommendations,
  };
}

function generateMockSimilarCases(proposedUse: string): SDABCase[] {
  const baseDecisions: Array<'APPROVED' | 'REFUSED' | 'APPROVED_WITH_CONDITIONS'> = [
    'APPROVED_WITH_CONDITIONS',
    'APPROVED',
    'APPROVED_WITH_CONDITIONS',
    'REFUSED',
    'APPROVED',
  ];

  return baseDecisions.slice(0, 3).map((decision, i) => ({
    id: `sdab-${2024 - i}-${1000 + i}`,
    case_number: `SDAB${2024 - i}-${String(1000 + i).padStart(4, '0')}`,
    address: `${100 + i * 10} ${['1st', '2nd', '3rd'][i]} ST SW`,
    decision_date: `${2024 - i}-${String(6 + i).padStart(2, '0')}-15`,
    decision,
    property_type: proposedUse.replace(/_/g, ' '),
    variance_type: ['setback', 'height', 'lot coverage', 'parking'][i % 4],
    conditions: decision === 'APPROVED_WITH_CONDITIONS'
      ? ['Landscaping must be completed within 6 months', 'Privacy screening required on south side']
      : undefined,
    reasons: decision === 'REFUSED'
      ? ['Incompatible with neighbourhood character', 'Insufficient parking for proposed use']
      : undefined,
  }));
}

export async function findSimilarCases(
  criteria: {
    property_type: string;
    variance_type: string;
    location?: string;
  }
): Promise<SDABCase[]> {
  // Generate mock similar cases (API endpoint not implemented yet)
  return generateMockSimilarCases(criteria.property_type);
}

// ============================================
// Quantity Survey API
// ============================================

export async function createQuantitySurvey(
  project: Omit<QuantitySurveyProject, 'id' | 'created_at'>
): Promise<QuantitySurveyProject> {
  return fetchApi('/api/v1/quantity-survey/projects', {
    method: 'POST',
    body: JSON.stringify(project),
  });
}

export async function generateCostEstimate(
  projectId: string,
  options?: {
    include_labor?: boolean;
    contingency_percent?: number;
  }
): Promise<CostEstimate> {
  return fetchApi(`/api/v1/quantity-survey/estimate/${projectId}`, {
    method: 'POST',
    body: JSON.stringify(options || {}),
  });
}

export async function exportBillOfQuantities(
  projectId: string,
  format: 'pdf' | 'excel'
): Promise<Blob> {
  const response = await fetch(
    `${baseUrl}/api/v1/quantity-survey/export/${projectId}?format=${format}`
  );
  if (!response.ok) throw new Error('Export failed');
  return response.blob();
}

// ============================================
// Feasibility Study API
// ============================================

export async function getGovernmentIncentives(
  category?: string
): Promise<GovernmentIncentive[]> {
  const params = category ? `?category=${category}` : '';
  return fetchApi(`/api/v1/feasibility/incentives${params}`);
}

export async function generateFeasibilityReport(
  request: {
    project_id: string;
    materials: Array<{
      category: string;
      specifications: string;
      quantity: number;
    }>;
    project_timeline_days: number;
    budget_cad: number;
  }
): Promise<FeasibilityReport> {
  return fetchApi('/api/v1/feasibility/analyze', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

export async function compareMaterialSources(
  material: {
    category: string;
    specifications: string;
    quantity: number;
  }
): Promise<ApiResponse<{
  comparisons: Array<{
    country: string;
    unit_cost: number;
    total_landed_cost: number;
    lead_time_days: number;
  }>;
  recommendation: string;
}>> {
  return fetchApi('/api/v1/feasibility/compare', {
    method: 'POST',
    body: JSON.stringify(material),
  });
}

export async function calculateLeadTimes(
  materials: Array<{
    name: string;
    source_country: string;
    quantity: number;
  }>,
  projectStartDate: string
): Promise<ApiResponse<{
  schedule: Array<{
    material: string;
    order_by: string;
    expected_arrival: string;
    buffer_days: number;
  }>;
}>> {
  return fetchApi('/api/v1/feasibility/lead-times', {
    method: 'POST',
    body: JSON.stringify({ materials, project_start_date: projectStartDate }),
  });
}

// ============================================
// Address Autocomplete API
// ============================================

export interface AddressAutocompleteResult {
  address: string;
  community: string | null;
  zone_code: string | null;
  parcel_id: string | null;
  latitude: number | null;
  longitude: number | null;
}

export async function searchAddresses(
  query: string,
  limit: number = 10
): Promise<AddressAutocompleteResult[]> {
  const params = new URLSearchParams({ q: query, limit: String(limit) });
  return fetchApi(`/api/v1/addresses/autocomplete?${params}`);
}

// ============================================
// Permits API
// ============================================

export type PermitStatus =
  | 'draft'
  | 'submitted'
  | 'under_review'
  | 'deficiency_issued'
  | 'approved'
  | 'rejected'
  | 'expired'
  | 'cancelled';

export type PermitType =
  | 'building'
  | 'development'
  | 'demolition'
  | 'electrical'
  | 'plumbing'
  | 'mechanical'
  | 'gas';

export interface PermitApplication {
  id: string;
  application_number: string;
  permit_type: PermitType;
  status: PermitStatus;
  project_name: string;
  description?: string;
  address: string;
  parcel_id?: string;
  project_type?: string;
  occupancy_group?: string;
  construction_type?: string;
  estimated_value?: number;
  building_area_sqm?: number;
  building_height_storeys?: number;
  applicant?: {
    name: string;
    email?: string;
    phone?: string;
    company?: string;
  };
  created_at: string;
  updated_at: string;
  submitted_at?: string;
  decision_date?: string;
  documents_count: number;
  deficiencies_count: number;
  open_deficiencies_count: number;
}

export interface PermitDocument {
  id: string;
  permit_application_id: string;
  document_type: string;
  title: string;
  description?: string;
  filename: string;
  file_type?: string;
  file_size_bytes: number;
  status: 'pending' | 'approved' | 'rejected' | 'needs_revision';
  uploaded_at: string;
  reviewed_at?: string;
  reviewer?: string;
  review_notes?: string;
  verified?: boolean;
}

export interface PermitDeficiency {
  id: string;
  permit_application_id: string;
  category: string;
  deficiency_type?: string;
  title: string;
  description: string;
  priority: 'critical' | 'major' | 'minor';
  severity?: 'high' | 'medium' | 'low';
  code_reference?: string;
  status: 'open' | 'addressed' | 'resolved' | 'waived';
  resolution_notes?: string;
  created_at: string;
  created_by?: string;
  deadline?: string;
}

export interface PermitComment {
  id: string;
  permit_application_id: string;
  category: string;
  comment: string;
  content?: string;
  code_reference?: string;
  reviewer?: string;
  author_name?: string;
  author_role?: string;
  created_at: string;
  is_resolved: boolean;
  is_internal?: boolean;
}

export interface PermitTimelineEvent {
  id?: string;
  title?: string;
  timestamp: string;
  event_type: string;
  description: string;
  user?: string;
  created_at?: string;
  created_by?: string;
  details?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
}

export interface PermitStatistics {
  total_applications: number;
  pending_review: number;
  by_status: Record<string, number>;
  by_type: Record<string, number>;
  average_review_days?: number;
  approval_rate?: number;
}

export interface CreatePermitApplicationInput {
  permit_type: PermitType;
  project_name: string;
  description?: string;
  address: string;
  parcel_id?: string;
  project_type?: string;
  occupancy_group?: string;
  construction_type?: string;
  estimated_value?: number;
  building_area_sqm?: number;
  building_height_storeys?: number;
  applicant?: {
    name: string;
    email?: string;
    phone?: string;
    company?: string;
  };
}

export interface UpdatePermitApplicationInput {
  project_name?: string;
  description?: string;
  address?: string;
  project_type?: string;
  estimated_value?: number;
  building_area_sqm?: number;
  building_height_storeys?: number;
  applicant?: {
    name: string;
    email?: string;
    phone?: string;
    company?: string;
  };
}

export interface PermitApplicationsListParams {
  status?: PermitStatus;
  permit_type?: PermitType;
  search?: string;
  page?: number;
  limit?: number;
}

export interface PermitsListResponse {
  items: PermitApplication[];
  total: number;
  page: number;
  pages: number;
}

// Permits API functions
export const permitsApi = {
  createApplication: (data: CreatePermitApplicationInput): Promise<PermitApplication> => {
    return fetchApi('/api/v1/permits/applications', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  listApplications: (params?: PermitApplicationsListParams): Promise<PermitsListResponse> => {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set('status', params.status);
    if (params?.permit_type) searchParams.set('permit_type', params.permit_type);
    if (params?.search) searchParams.set('address', params.search);
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('page_size', String(params.limit));
    return fetchApi(`/api/v1/permits/applications?${searchParams}`);
  },

  getApplication: (id: string): Promise<PermitApplication> => {
    return fetchApi(`/api/v1/permits/applications/${id}`);
  },

  updateApplication: (id: string, data: UpdatePermitApplicationInput): Promise<PermitApplication> => {
    return fetchApi(`/api/v1/permits/applications/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  submitApplication: (id: string): Promise<PermitApplication> => {
    return fetchApi(`/api/v1/permits/applications/${id}/submit`, {
      method: 'POST',
    });
  },

  updateStatus: (id: string, newStatus: PermitStatus, notes?: string): Promise<PermitApplication> => {
    return fetchApi(`/api/v1/permits/applications/${id}/status`, {
      method: 'POST',
      body: JSON.stringify({ new_status: newStatus, notes }),
    });
  },

  getTimeline: (id: string): Promise<{ events: PermitTimelineEvent[] }> => {
    return fetchApi(`/api/v1/permits/applications/${id}/timeline`);
  },

  uploadDocument: async (id: string, file: File, documentType: string, title?: string): Promise<PermitDocument> => {
    const formData = new FormData();
    formData.append('file', file);
    const params = new URLSearchParams({ document_type: documentType });
    if (title) params.set('title', title);

    const response = await fetch(`${baseUrl}/api/v1/permits/applications/${id}/documents?${params}`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Upload failed');
    }

    return response.json();
  },

  listDocuments: (id: string): Promise<PermitDocument[]> => {
    return fetchApi(`/api/v1/permits/applications/${id}/documents`);
  },

  removeDocument: (applicationId: string, documentId: string): Promise<void> => {
    return fetchApi(`/api/v1/permits/applications/${applicationId}/documents/${documentId}`, {
      method: 'DELETE',
    });
  },

  listDeficiencies: (id: string): Promise<PermitDeficiency[]> => {
    return fetchApi(`/api/v1/permits/applications/${id}/deficiencies`);
  },

  listComments: (id: string): Promise<PermitComment[]> => {
    return fetchApi(`/api/v1/permits/applications/${id}/comments`);
  },

  getStatistics: (): Promise<PermitStatistics> => {
    return fetchApi('/api/v1/permits/statistics');
  },

  getDocumentTypes: (): Promise<Record<string, { name: string; description: string }>> => {
    return fetchApi('/api/v1/permits/document-types');
  },

  getUploadConstraints: (): Promise<{
    max_file_size_bytes: number;
    max_file_size_mb: number;
    allowed_extensions: string[];
  }> => {
    return fetchApi('/api/v1/permits/upload-constraints');
  },
};
